import { ButtonBase, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { ReactElement, useMemo, useState } from 'react';

import {
  getSubscriptionDisplayName,
  getSubscriptionTypeDisplayName,
} from '$shared/utils/subscription.util';

import { SubscriptionDialog } from '$components/subscription-dialog/subscription-dialog';
import { useGetDeviceUsage } from '$features/devices/devices.api';
import { useGetSubscription } from '$features/subscription/subscription-api';
import './subscription-header.scss';

export function SubscriptionHeader({
  showDeviceUsage,
}: {
  showDeviceUsage: boolean;
}): ReactElement {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: subscriptionData, isLoading: isSubscriptionLoading } =
    useGetSubscription();
  const { data: deviceUsageData, isLoading: isDeviceUsageLoading } =
    useGetDeviceUsage(showDeviceUsage);

  const subscriptionInformation = useMemo(
    () => ({
      name: subscriptionData?.name || '',
      subscription: getSubscriptionDisplayName(subscriptionData?.subscription),
      subscriptionType: getSubscriptionTypeDisplayName(
        subscriptionData?.subscriptionType,
      ),
      licensedDeviceCount: subscriptionData?.subscribedDeviceCount,
      usedDeviceCount: showDeviceUsage ? deviceUsageData?.usedDeviceCount : undefined,
    }),
    [
      subscriptionData?.name,
      subscriptionData?.subscription,
      subscriptionData?.subscriptionType,
      subscriptionData?.subscribedDeviceCount,
      deviceUsageData?.usedDeviceCount,
      showDeviceUsage,
    ],
  );

  const isOverLimit =
    showDeviceUsage &&
    !isSubscriptionLoading &&
    !isDeviceUsageLoading &&
    subscriptionData?.subscribedDeviceCount !== undefined &&
    deviceUsageData?.usedDeviceCount !== undefined &&
    deviceUsageData.usedDeviceCount > subscriptionData.subscribedDeviceCount;

  if (isSubscriptionLoading || (showDeviceUsage && isDeviceUsageLoading)) {
    return <div />;
  }

  const content = (
    <>
      <Icon
        name="organization"
        size={IconSizeClasses.XSmall}
        className="m-right-s subscription-header-icon"
      />
      <p className="font-weight-medium m-right-m">{subscriptionInformation.name}</p>
      {subscriptionInformation.subscription && (
        <div className="font-weight-medium btn-base btn btn--outline btn--info btn--pill p-y-xs p-x-m subscription-header-tag color-gray-900">
          <span className="font-size-s">
            {subscriptionInformation.subscription}{' '}
            {subscriptionInformation.subscriptionType}
          </span>
          {isOverLimit && (
            <Icon
              name="alert"
              size={IconSizeClasses.XXSmall}
              className="m-left-xs bg-red-500"
            />
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      {showDeviceUsage ? (
        <ButtonBase
          className="flex flex__jc--space-between flex__ai--center subscription-header"
          onClick={() => setIsDialogOpen(true)}
        >
          {content}
        </ButtonBase>
      ) : (
        <div className="flex flex__jc--space-between flex__ai--center">{content}</div>
      )}
      {showDeviceUsage && (
        <SubscriptionDialog
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          organizationName={subscriptionInformation.name}
          subscription={subscriptionInformation.subscription}
          subscriptionType={subscriptionInformation.subscriptionType}
          licensedDevices={subscriptionInformation.licensedDeviceCount}
          usedDevices={subscriptionInformation.usedDeviceCount}
        />
      )}
    </>
  );
}
